import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Clock, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { assessmentApi } from '../services/api';
import { useAuthStore } from '../store/authStore';
import toast from 'react-hot-toast';

function AssessmentView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [assessment, setAssessment] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [attemptId, setAttemptId] = useState(null);
  const [responses, setResponses] = useState({});
  const [result, setResult] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [timeLeft, setTimeLeft] = useState(null);
  const timerRef = useRef(null);

  useEffect(() => {
    loadAssessment();
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [id]);

  const loadAssessment = async () => {
    try {
      const res = await assessmentApi.getAssessment(id);
      setAssessment(res.data);
    } catch (error) {
      if (error.response?.data?.code === 'LEVEL_REQUIRED') {
        toast.error('Upgrade your plan to access this assessment');
      } else {
        toast.error('Failed to load assessment');
      }
      navigate(-1);
    } finally {
      setIsLoading(false);
    }
  };

  const handleStartAttempt = async () => {
    try {
      const res = await assessmentApi.startAttempt(id);
      setAttemptId(res.data.attemptId);

      if (assessment.time_limit_minutes) {
        setTimeLeft(assessment.time_limit_minutes * 60);
        timerRef.current = setInterval(() => {
          setTimeLeft(prev => {
            if (prev <= 1) {
              clearInterval(timerRef.current);
              handleSubmit();
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to start attempt');
    }
  };

  const handleResponseChange = (questionId, value, type) => {
    setResponses(prev => ({
      ...prev,
      [questionId]: type === 'mcq'
        ? { ...prev[questionId], selectedOption: value }
        : { ...prev[questionId], answerText: value }
    }));
  };

  const handleSubmit = async () => {
    if (!attemptId) return;
    setIsSubmitting(true);

    try {
      const formattedResponses = Object.entries(responses).map(([questionId, data]) => ({
        questionId,
        selectedOption: data.selectedOption || null,
        answerText: data.answerText || null
      }));

      const res = await assessmentApi.submitAttempt(attemptId, formattedResponses);
      setResult(res.data);
      if (timerRef.current) clearInterval(timerRef.current);

      // Load attempt details for full results
      const attemptRes = await assessmentApi.getAttempt(attemptId);
      setResult(prev => ({ ...prev, attempt: attemptRes.data }));
    } catch (error) {
      toast.error('Failed to submit assessment');
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!assessment) return null;

  // Results view
  if (result) {
    return (
      <div className="p-6 max-w-3xl mx-auto">
        <button onClick={() => navigate(-1)} className="flex items-center text-gray-600 hover:text-gray-900 mb-4">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </button>

        <div className="bg-white border border-gray-200 rounded-xl p-8 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">{assessment.title}</h2>
          <p className="text-gray-500 mb-6">{result.message}</p>

          {result.score !== null && result.score !== undefined && (
            <div className="mb-6">
              <div className="text-5xl font-bold text-primary-600">{result.percentage}%</div>
              <p className="text-gray-500 mt-1">{result.score} / {result.maxScore} points</p>
            </div>
          )}

          {result.status === 'submitted' && (
            <div className="flex items-center justify-center text-amber-600 mb-6">
              <AlertCircle className="w-5 h-5 mr-2" />
              <span>Subjective answers pending review</span>
            </div>
          )}

          {/* Question-by-question results */}
          {result.attempt?.responses && (
            <div className="text-left mt-8 space-y-4">
              <h3 className="font-semibold text-gray-900">Results</h3>
              {result.attempt.responses.map((r, i) => (
                <div key={i} className={`p-4 rounded-lg border ${
                  r.is_correct === true ? 'border-green-200 bg-green-50' :
                  r.is_correct === false ? 'border-red-200 bg-red-50' :
                  'border-gray-200 bg-gray-50'
                }`}>
                  <div className="flex items-start">
                    {r.is_correct === true ? (
                      <CheckCircle className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    ) : r.is_correct === false ? (
                      <XCircle className="w-5 h-5 text-red-600 mr-2 mt-0.5 flex-shrink-0" />
                    ) : (
                      <AlertCircle className="w-5 h-5 text-amber-500 mr-2 mt-0.5 flex-shrink-0" />
                    )}
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{r.question_text}</p>
                      {r.question_type === 'mcq' && (
                        <p className="text-sm text-gray-600 mt-1">
                          Your answer: {r.selected_option}
                          {r.correct_answer && ` | Correct: ${r.correct_answer}`}
                        </p>
                      )}
                      {r.question_type === 'subjective' && r.answer_text && (
                        <p className="text-sm text-gray-600 mt-1">Your answer: {r.answer_text}</p>
                      )}
                      {r.explanation && (
                        <p className="text-sm text-primary-600 mt-1">{r.explanation}</p>
                      )}
                      <p className="text-xs text-gray-500 mt-1">
                        {r.points_awarded !== null ? `${r.points_awarded}/${r.points} points` : 'Pending review'}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Pre-start view
  if (!attemptId) {
    return (
      <div className="p-6 max-w-3xl mx-auto">
        <button onClick={() => navigate(-1)} className="flex items-center text-gray-600 hover:text-gray-900 mb-4">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </button>

        <div className="bg-white border border-gray-200 rounded-xl p-8">
          <h1 className="text-2xl font-bold text-gray-900">{assessment.title}</h1>
          {assessment.description && (
            <p className="text-gray-600 mt-2">{assessment.description}</p>
          )}

          <div className="grid grid-cols-2 gap-4 mt-6 text-sm">
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-gray-500">Questions</p>
              <p className="font-semibold">{assessment.questions?.length || 0}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-gray-500">Type</p>
              <p className="font-semibold capitalize">{assessment.assessment_type}</p>
            </div>
            {assessment.time_limit_minutes && (
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-gray-500">Time Limit</p>
                <p className="font-semibold">{assessment.time_limit_minutes} minutes</p>
              </div>
            )}
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-gray-500">Max Attempts</p>
              <p className="font-semibold">
                {assessment.max_attempts > 0 ? `${assessment.attemptCount || 0}/${assessment.max_attempts}` : 'Unlimited'}
              </p>
            </div>
          </div>

          <button
            onClick={handleStartAttempt}
            className="mt-6 w-full py-3 bg-primary-600 text-white rounded-lg font-semibold hover:bg-primary-700 transition-colors"
          >
            Start Assessment
          </button>
        </div>
      </div>
    );
  }

  // Active quiz view
  return (
    <div className="p-6 max-w-3xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-bold text-gray-900">{assessment.title}</h1>
        {timeLeft !== null && (
          <div className={`flex items-center px-3 py-1.5 rounded-lg text-sm font-medium ${
            timeLeft < 60 ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'
          }`}>
            <Clock className="w-4 h-4 mr-1" />
            {formatTime(timeLeft)}
          </div>
        )}
      </div>

      <div className="space-y-6">
        {assessment.questions?.map((q, index) => (
          <div key={q.id} className="bg-white border border-gray-200 rounded-lg p-5">
            <p className="font-medium text-gray-900 mb-3">
              <span className="text-primary-600 mr-2">Q{index + 1}.</span>
              {q.question_text}
            </p>
            <p className="text-xs text-gray-400 mb-3">{q.points} point{q.points !== 1 ? 's' : ''} | {q.question_type.toUpperCase()}</p>

            {q.question_type === 'mcq' && q.options && (
              <div className="space-y-2">
                {q.options.map(opt => (
                  <label
                    key={opt.key}
                    className={`flex items-center p-3 rounded-lg border cursor-pointer transition-colors ${
                      responses[q.id]?.selectedOption === opt.key
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name={`q_${q.id}`}
                      value={opt.key}
                      checked={responses[q.id]?.selectedOption === opt.key}
                      onChange={(e) => handleResponseChange(q.id, e.target.value, 'mcq')}
                      className="mr-3"
                    />
                    <span className="font-medium text-gray-600 mr-2">{opt.key}.</span>
                    <span className="text-gray-800">{opt.text}</span>
                  </label>
                ))}
              </div>
            )}

            {q.question_type === 'subjective' && (
              <textarea
                value={responses[q.id]?.answerText || ''}
                onChange={(e) => handleResponseChange(q.id, e.target.value, 'subjective')}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="Type your answer..."
              />
            )}
          </div>
        ))}
      </div>

      <button
        onClick={handleSubmit}
        disabled={isSubmitting}
        className="mt-6 w-full py-3 bg-primary-600 text-white rounded-lg font-semibold hover:bg-primary-700 disabled:opacity-50 transition-colors"
      >
        {isSubmitting ? 'Submitting...' : 'Submit Assessment'}
      </button>
    </div>
  );
}

export default AssessmentView;
